import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

const GIPHY_API_KEY = "GlVGYHkr3WSBnllca54iNt0yFbjz7L65";
const GIPHY_SEARCH_URL = "https://api.giphy.com/v1/gifs/search";
const GIPHY_TRENDING_URL = "https://api.giphy.com/v1/gifs/trending";

interface GifResult {
  id: string;
  images: {
    fixed_height_small: { url: string };
    fixed_height: { url: string };
    original: { url: string };
  };
}

interface Props {
  open: boolean;
  onSelect: (url: string) => void;
  onClose: () => void;
}

const GifPicker = ({ open, onSelect, onClose }: Props) => {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [gifs, setGifs] = useState<GifResult[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchGifs = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const url = query.trim()
        ? `${GIPHY_SEARCH_URL}?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query)}&limit=24&rating=g`
        : `${GIPHY_TRENDING_URL}?api_key=${GIPHY_API_KEY}&limit=24&rating=g`;
      const res = await fetch(url);
      const json = await res.json();
      setGifs(json.data || []);
    } catch {
      setGifs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchGifs("");
      setSearch("");
    }
  }, [open, fetchGifs]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => fetchGifs(search), 400);
    return () => clearTimeout(timer);
  }, [search, open, fetchGifs]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={
          isMobile
            ? "rounded-t-2xl !p-0 max-h-[80vh] flex flex-col overflow-hidden"
            : "!p-0 h-full w-full sm:max-w-md flex flex-col overflow-hidden"
        }
      >
        <div className="px-4 pt-4 pb-2 flex-shrink-0">
          <SheetHeader className="pb-3">
            <SheetTitle className="text-base font-semibold text-center">Choose a GIF</SheetTitle>
          </SheetHeader>

          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search GIFs..."
              className="pl-9 text-sm border-border"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4">
          <div className="grid grid-cols-4 gap-1.5 pb-2">
            {loading ? (
              <>
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-lg" />
                ))}
              </>
            ) : gifs.length === 0 ? (
              <p className="col-span-4 text-center text-sm text-muted-foreground py-6">No GIFs found</p>
            ) : (
              gifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => { onSelect(gif.images.fixed_height.url); onClose(); }}
                  className="rounded-lg overflow-hidden border border-border hover:border-foreground/30 transition-colors aspect-square"
                >
                  <img src={gif.images.fixed_height_small.url} alt="GIF" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))
            )}
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground text-center py-3 flex-shrink-0">Powered by GIPHY</p>
      </SheetContent>
    </Sheet>
  );
};

export default GifPicker;
